/* eslint-disable @typescript-eslint/no-explicit-any */
import Card from '@mui/material/Card'
import Paper from '@mui/material/Paper'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { Formik } from 'formik'
import React, { useEffect } from 'react'

import content from './content'
import schema from './schema'
import Form from '../../../components/forms/elements/Form'
import FormButton from '../../../components/forms/elements/FormButton'
import FormButtonContainer from '../../../components/forms/elements/FormButtonContainer'
import FormGrid from '../../../components/forms/elements/FormGrid'
import useFormRefundByUser from '../../../hooks/useFormRefundByUser'
import Items from '../summary/items'

interface Props {
  event: any
  user: any
  updateData: (values) => any
  onBack?: React.MouseEventHandler<HTMLButtonElement>
  onCancel?: React.MouseEventHandler<HTMLButtonElement>
  editing?: boolean
  setLoading?: (value: boolean) => void
  displayError?: (values: any) => void
}

const AccountForm: React.FC<Props> = ({
  event,
  user: initUser,
  updateData,
  onBack,
  onCancel,
  editing,
  setLoading,
  displayError,
}) => {
  const theme = useTheme()
  const user = initUser

  const { user: existingUser, apiLoading } = useFormRefundByUser({
    eventId: event.id,
    email: user.email,
  })

  useEffect(() => {
    setLoading(apiLoading)
  }, [apiLoading])

  return (
    <>
      {existingUser ? (
        <>
          <Paper sx={{ padding: theme.spacing(2), mb: theme.spacing(4) }}>
            <Typography variant="body1" color="error" mb={2}>
              We have already captured your refund info:
            </Typography>
            <Items data={existingUser} />
            <br />
            <Typography variant="caption" my={4} color="secondary">
              <strong>NOTE: </strong>
              Feel free to contact for support@megatechph.com for inquires or
              assistance.
            </Typography>
          </Paper>
          <FormButton secondary outlined onClick={onCancel}>
            Back
          </FormButton>
        </>
      ) : (
        <Formik
          initialValues={initUser}
          validateOnMount={editing}
          enableReinitialize
          validationSchema={schema}
          onSubmit={(values, actions) => {
            setLoading(true)

            Object.keys(values).forEach((key) => {
              if (typeof values[key] === 'string')
                values[key] = values[key]?.trim()
            })
            const [, error] = updateData(values)
            if (error) {
              displayError({ title: error.title, message: error.message })
            }
            setLoading(false)
            actions.setSubmitting(false)
          }}>
          {(form) => {
            const updateField = (key, value) => {
              form.setFieldValue(key, value)
              form.setTouched({
                ...form.touched,
                [key]: user[key] !== value,
              })
              form.validateField('bankName')
              form.validateField('accountName')
              form.validateField('accountNumber')
            }

            const handlePaymentModeChange = (e) => {
              updateField('paymentMode', e.target.value)
            }

            return (
              <Form id="refund-form">
                <Card>
                  <FormGrid
                    content={content({
                      values: form.values,
                      handlePaymentModeChange,
                      formDisabled: form.isSubmitting || apiLoading,
                    })}
                  />
                </Card>
                <FormButtonContainer wrap={false}>
                  <FormButton
                    secondary
                    outlined
                    onClick={onBack}
                    disabled={form.isSubmitting}>
                    Back
                  </FormButton>
                  <FormButton
                    submit
                    primary
                    outlined
                    disabled={!form.isValid || form.isSubmitting || apiLoading}>
                    Next
                  </FormButton>
                </FormButtonContainer>
              </Form>
            )
          }}
        </Formik>
      )}
    </>
  )
}

export default AccountForm
